import React from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, useIntl } from 'react-intl';
import { loadLanguagePack } from '@americanexpress/one-app-ducks';
import { useDispatch, useSelector } from 'react-redux';
import {
  Accordion,
  AccordionContent,
  AccordionPanel,
  Button,
  IconButton,
  SelectNative,
  SelectNativeOption,
} from '@americanexpress/dls-react';
import { IconClose, IconSuccess } from '@americanexpress/dls-icons';
import UploadZone from '../upload/UploadZone';
import DocList from '../upload/DocList';
import FileRequirements from '../messages/FileRequirements';
import Submit from '../upload/Submit';
import ResponseMessages from '../messages/ResponseMessages';
import DeleteConfirmation from '../upload/DeleteConfirmation';
import createDocUpload from '../../utils/createDocUpload';
import {
  deleteDocument as deleteDocumentApi,
  submitDocument as submitDocumentApi,
  uploadDocument as uploadDocumentApi,
} from '../../utils/docUploadApi';
import {
  calculateDocsTotalSize,
  removeDocByFileId,
  removeDocByIndex,
  getEmptyStatusMessage,
  getRejectedFileStatus,
} from '../../utils/docUploadCommon';
import {
  DEFAULT_KYC_DOCUMENT_SECTIONS,
  getMockIdTypeMetadata,
  getMockKycDocumentSections,
} from '../../utils/mockKycDocumentMetadataApi';
import '../DocUploadStyles.scss';

// Normalizes API-provided ID type metadata so UI rendering can assume
// a stable shape: string id/label and at least one side.
const normalizeIdTypeMetadata = (docTypes) => {
  if (!Array.isArray(docTypes)) {
    return [];
  }

  return docTypes
    .filter(
      (docType) => docType
        && typeof docType.id === 'string'
        && typeof docType.label === 'string'
    )
    .map((docType) => ({
      id: docType.id,
      label: docType.label,
      sides:
        Array.isArray(docType.sides) && docType.sides.length > 0
          ? docType.sides.map((side) => String(side).toLowerCase())
          : ['front'],
    }));
};

// Normalizes KYC section metadata and keeps only fields used by the UI.
const normalizeKycDocumentSections = (sections) => {
  if (!Array.isArray(sections)) {
    return [];
  }

  return sections
    .filter((section) => section && typeof section.id === 'string')
    .map((section) => ({
      id: section.id,
      label: typeof section.label === 'string' ? section.label : null,
      labelMessageId:
        typeof section.labelMessageId === 'string'
          ? section.labelMessageId
          : null,
    }));
};

// Resolves section heading precedence: explicit label, i18n message id,
// then fallback plain text.
const renderSectionLabel = (section, fallbackMessageId, fallbackLabel) => {
  if (section && typeof section.label === 'string') {
    return section.label;
  }

  const messageId = section?.labelMessageId || fallbackMessageId;
  if (messageId) {
    return <FormattedMessage id={messageId} defaultMessage={fallbackLabel} />;
  }

  return fallbackLabel;
};

const formatSectionFallbackLabel = (sectionId) => String(sectionId)
  .split('-')
  .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
  .join(' ');

const formatSideFallbackLabel = (side) => String(side)
  .split('-')
  .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
  .join(' ');

const DEFAULT_ACCEPTED_FILE_TYPES = '.bmp,.doc,.docx,.gif,.jpeg,.jpg,.pdf,.png,.tif,.tiff,.xls,.xlsx';
const DEFAULT_MAX_FILE_SIZE = 30000000;
const DEFAULT_MAX_BATCH_SIZE = 100000000;
const DEFAULT_MAX_SIZE_LABEL = '30 MB';
const DEFAULT_MAX_BATCH_SIZE_LABEL = '100 MB';
const DEFAULT_UPLOAD_ZONE_VIEW = 'ViewOne';
const DEFAULT_FILE_REQ_VIEW = 'ViewOne';
const DEFAULT_DOC_LIST_VIEW = 'ViewProofKyc';
const DEFAULT_SUBMIT_VIEW = 'ViewSubmitRight';

function DocUploadAlwaysKyc({
  maxFileSize,
  maxBatchSize,
  maxSizeLabel,
  maxBatchSizeLabel,
  fileTypes,
  fileTypesLabel,
  isSubmitDisabled,
  isUploadDisabled,
  submitPopLabel,
  uploadZoneView,
  fileReqView,
  docListView,
  idTypeMetadataApi,
  kycDocumentMetadataApi,
  formDataProps,
  docUploadUrl,
  onStartUploadCall,
  onUploadCall,
  onDeleteCall,
  onSubmitCall,
  filePreview,
  submitView,
  downloadProgress,
  sectionUploadModeById,
}) {
  const intl = useIntl();
  const dispatch = useDispatch();
  const locale = useSelector((state) => state.getIn(['intl', 'activeLocale']));

  // State for document management
  const [kycDocumentSections, setKycDocumentSections] = React.useState(
    kycDocumentMetadataApi ? [] : DEFAULT_KYC_DOCUMENT_SECTIONS
  );
  const [sectionAcceptedDocs, setSectionAcceptedDocs] = React.useState({});
  const [isAddingAnotherBySection, setIsAddingAnotherBySection] = React.useState({});
  const [expandedSectionIds, setExpandedSectionIds] = React.useState({});
  const [documentId, setDocumentId] = React.useState('');

  // ID Proof specific state - RESTORED FOR UI
  const [selectedIdType, setSelectedIdType] = React.useState('');
  const [idTypeOptions, setIdTypeOptions] = React.useState([]);
  const [isIdProofExpanded, setIsIdProofExpanded] = React.useState(false);
  const [currentIdProofDocsBySide, setCurrentIdProofDocsBySide] = React.useState({});
  const [savedIdProofs, setSavedIdProofs] = React.useState([]);
  const [isAddingAnotherIdProof, setIsAddingAnotherIdProof] = React.useState(false);

  // State for UI interactions
  const [statusMessage, setStatusMessage] = React.useState(getEmptyStatusMessage());
  const [statusSectionId, setStatusSectionId] = React.useState('');
  const [isDeleting, setIsDeleting] = React.useState(false);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [showPopup, setShowPopup] = React.useState({});

  React.useEffect(() => {
    dispatch(loadLanguagePack('axp-document-uploader', { fallbackLocale: 'en-US' }));
  }, [dispatch, locale]);

  React.useEffect(() => {
    let isMounted = true;

    const loadKycDocumentMetadata = async () => {
      try {
        const [sections, idTypes] = await Promise.all([
          kycDocumentMetadataApi
            ? kycDocumentMetadataApi()
            : getMockKycDocumentSections(),
          idTypeMetadataApi ? idTypeMetadataApi() : getMockIdTypeMetadata(),
        ]);

        if (!isMounted) {
          return;
        }

        setIdTypeOptions(normalizeIdTypeMetadata(idTypes));
        setKycDocumentSections(normalizeKycDocumentSections(sections));
      } catch {
        if (isMounted) {
          setIdTypeOptions(
            idTypeMetadataApi ? [] : normalizeIdTypeMetadata([])
          );
          setKycDocumentSections(
            kycDocumentMetadataApi ? [] : DEFAULT_KYC_DOCUMENT_SECTIONS
          );
        }
      }
    };

    loadKycDocumentMetadata();

    return () => {
      isMounted = false;
    };
  }, [idTypeMetadataApi, kycDocumentMetadataApi]);

  // Initialize document on mount
  React.useEffect(() => {
    if (isUploadDisabled) {
      return;
    }

    const formData = new FormData();
    if (formDataProps) {
      Object.entries(formDataProps).forEach(([key, value]) => {
        formData.append(key, value);
      });
    }
    formData.append('documentId', 'INITIALDOC');

    createDocUpload(dispatch, formData, docUploadUrl)
      .then((res) => {
        if (res.status && res.status.statusCode === '50200') {
          setDocumentId(res.documentId);
          if (onUploadCall) {
            onUploadCall(res);
          }
        } else {
          setStatusMessage({
            message: 'uploadApiError',
            type: 'error',
          });
        }
      })
      .catch(() => {
        setStatusMessage({
          message: 'uploadApiError',
          type: 'error',
        });
      });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- initialize upload once on mount

  const onResetMessages = () => {
    setStatusMessage(getEmptyStatusMessage());
    setStatusSectionId('');
  };

  const setScopedStatusMessage = (message, sectionId = '') => {
    setStatusMessage(message);
    setStatusSectionId(sectionId);
  };

  // Section upload rule resolution:
  // - explicit map config wins,
  // - supporting-document stays multi-upload by default for backward compatibility,
  // - all other sections default to single-upload.
  const isSectionSingleUpload = (sectionId) => {
    const sectionMode = sectionUploadModeById?.[sectionId];
    if (sectionMode === 'multiple') {
      return false;
    }
    if (sectionMode === 'single') {
      return true;
    }
    // Keep supporting-document on existing multi-upload behavior by default.
    if (sectionId === 'supporting-document') {
      return false;
    }

    // Default to single-upload for other KYC standard sections unless explicitly set to multiple.
    return true;
  };

  const getSectionAcceptedDocs = (sectionId) => sectionAcceptedDocs[sectionId] || [];

  // Creates a deterministic temporary file id so progress updates can target
  // the correct list item before the API returns the persisted fileId.
  const createPendingDoc = (file, sectionId) => ({
    name: file.name,
    size: file.size,
    type: file.type,
    fileId: [
      'pending',
      file.name,
      file.size,
      file.lastModified,
      file.type,
      sectionId,
    ].join('-'),
    progress: 0,
    sectionId,
  });

  const uploadDocsWithProgress = ({
    acceptedFiles,
    sectionId,
  }) => {
    // Optimistically add pending docs so users see immediate upload feedback.
    const pendingDocs = acceptedFiles.map((file) => createPendingDoc(file, sectionId));

    setSectionAcceptedDocs((previousDocsBySection) => {
      const existingDocs = previousDocsBySection[sectionId] || [];
      return {
        ...previousDocsBySection,
        [sectionId]: [...existingDocs, ...pendingDocs],
      };
    });

    const uploads = pendingDocs.map((pendingDoc, index) => uploadDocumentApi({
      docUploadUrl,
      formDataProps,
      file: acceptedFiles[index],
      documentId: documentId || 'INITIALDOC',
      onStartUploadCall,
      onProgress: (event) => {
        setSectionAcceptedDocs((previousDocsBySection) => ({
          ...previousDocsBySection,
          [sectionId]: (previousDocsBySection[sectionId] || []).map((doc) => {
            if (doc.fileId === pendingDoc.fileId) {
              return {
                ...doc,
                progress: Number((event.loaded / event.total) * 100).toFixed(0),
              };
            }
            return doc;
          }),
        }));
      },
    }).then(({ response: res, outcome: uploadOutcome }) => {
      if (uploadOutcome !== 'success') {
        return null;
      }

      // Keep the first successful document id for all subsequent uploads/deletes/submits.
      setDocumentId((currentDocumentId) => currentDocumentId || res.documentId);

      if (onUploadCall) {
        onUploadCall(res);
      }

      setSectionAcceptedDocs((previousDocsBySection) => ({
        ...previousDocsBySection,
        [sectionId]: (previousDocsBySection[sectionId] || []).map((doc) => {
          if (doc.fileId === pendingDoc.fileId) {
            return {
              ...doc,
              fileId: res.fileList[res.fileList.length - 1].fileId,
              progress: null,
            };
          }
          return doc;
        }),
      }));

      return res;
    }).catch(() => {
      // Remove only failed pending doc; other uploads in the same drop continue.
      setSectionAcceptedDocs((previousDocsBySection) => ({
        ...previousDocsBySection,
        [sectionId]: (previousDocsBySection[sectionId] || []).filter(
          (doc) => doc.fileId !== pendingDoc.fileId
        ),
      }));
    }));

    return Promise.all(uploads);
  };

  const handleSectionOnDrop = (sectionId) => (acceptedFiles, rejectedFiles = []) => {
    onResetMessages();

    if (rejectedFiles.length > 0) {
      setScopedStatusMessage(
        getRejectedFileStatus(rejectedFiles[0], maxFileSize, maxSizeLabel),
        sectionId
      );
    }

    if (!Array.isArray(acceptedFiles) || acceptedFiles.length === 0) {
      return Promise.resolve([]);
    }

    const isSingleUpload = isSectionSingleUpload(sectionId);
    const hasExistingDocs = getSectionAcceptedDocs(sectionId).length > 0;
    const isAddingAnother = Boolean(isAddingAnotherBySection[sectionId]);

    if (isSingleUpload) {
      // Guardrail for single-upload sections:
      // only one file is allowed unless user explicitly chose "Add Another".
      if (acceptedFiles.length > 1 || (hasExistingDocs && !isAddingAnother)) {
        setScopedStatusMessage({
          message: 'multiFileError',
          type: 'error',
        }, sectionId);
        return Promise.resolve([]);
      }
    }

    const uploadPromise = uploadDocsWithProgress({
      acceptedFiles,
      sectionId,
    });

    if (isSingleUpload) {
      return uploadPromise.finally(() => {
        setIsAddingAnotherBySection((previousState) => ({
          ...previousState,
          [sectionId]: false,
        }));
      });
    }

    return uploadPromise;
  };

  const handleSectionAccordionToggle = (sectionId) => () => {
    setExpandedSectionIds((previousExpandedSectionIds) => ({
      ...previousExpandedSectionIds,
      [sectionId]: !previousExpandedSectionIds[sectionId],
    }));
  };

  const deleteDoc = (sectionId, idx) => {
    onResetMessages();

    const targetDoc = getSectionAcceptedDocs(sectionId)[idx];
    if (!targetDoc) {
      return Promise.resolve(null);
    }

    setIsDeleting(true);

    return deleteDocumentApi({
      dispatch,
      documentId,
      fileId: targetDoc.fileId,
      formDataProps,
    }).then(({ response: res, outcome: deleteOutcome }) => {
      setIsDeleting(false);
      if (deleteOutcome === 'success') {
        setSectionAcceptedDocs((previousDocsBySection) => ({
          ...previousDocsBySection,
          [sectionId]: removeDocByIndex(
            previousDocsBySection[sectionId] || [],
            idx
          ),
        }));
        setIsAddingAnotherBySection((previousState) => ({
          ...previousState,
          [sectionId]: false,
        }));
        setShowPopup({
          idx: null,
          fileId: null,
          fileName: null,
          popupType: '',
        });
        if (onDeleteCall) {
          onDeleteCall(res);
        }
        return res;
      }
      setStatusMessage({ message: deleteOutcome, type: 'error' });
      return null;
    }).catch(() => {
      setIsDeleting(false);
      setStatusMessage({
        message: 'deleteApiError',
        type: 'error',
      });
      return null;
    });
  };

  const submitDoc = () => {
    onResetMessages();

    if (!documentId) {
      setStatusMessage({
        message: 'documentNotInitialized',
        type: 'error',
      });
      return null;
    }

    // Submission payload is represented by all uploaded docs in UI state:
    // saved ID proofs + in-progress ID proof entry + standard sections.
    const allDocs = [
      ...savedIdProofs.flatMap((proof) => proof.docs),
      ...Object.values(currentIdProofDocsBySide).flat(),
      ...Object.values(sectionAcceptedDocs).flat(),
    ];
    const totalSize = calculateDocsTotalSize(allDocs);

    if (totalSize === 0) {
      setStatusMessage({
        message: 'noDocumentsSelected',
        type: 'error',
      });
      return null;
    }

    if (isSubmitting || isDeleting) {
      return null;
    }

    setIsSubmitting(true);

    return submitDocumentApi({
      dispatch,
      documentId,
      formDataProps,
    }).then(({ response: res, outcome: submitOutcome }) => {
      if (submitOutcome === 'success') {
        setStatusMessage({
          message: 'submitSuccessLabel',
          type: 'success',
        });
        setIsSubmitting(false);
        setSectionAcceptedDocs({});
        setIsAddingAnotherBySection({});
        setCurrentIdProofDocsBySide({});
        setSavedIdProofs([]);
        setSelectedIdType('');

        if (onSubmitCall) {
          onSubmitCall(res);
        }

        return res;
      }
      setStatusMessage({
        message: submitOutcome,
        type: 'error',
      });
      setIsSubmitting(false);
      return null;
    }).catch(() => {
      setStatusMessage({
        message: 'submitApiError',
        type: 'error',
      });
      setIsSubmitting(false);
      return null;
    });
  };

  const handlePopupShow = (sectionId) => (...popupArgs) => {
    const [idx, fileId, fileName, show, popupType, confirm] = popupArgs;
    if (confirm) {
      // Unified confirm handler delegates by popup intent.
      setShowPopup({
        show,
        popupType,
      });
      if (popupType === 'delete') {
        return deleteDoc(sectionId, idx);
      }
      return submitDoc();
    }
    setShowPopup({
      show,
      idx,
      fileId,
      fileName,
      popupType,
      sectionId,
    });
    return null;
  };

  // ID Proof computed state acts as a small UI state machine. These flags decide
  // whether to show side-by-side slots, stacked slots, or uploaded list views.
  const selectedIdTypeConfig = idTypeOptions.find(
    (docType) => docType.id === selectedIdType
  );
  const selectedIdTypeLabel = selectedIdTypeConfig
    ? selectedIdTypeConfig.label
    : '';
  const selectedIdTypeSides = selectedIdTypeConfig
    ? selectedIdTypeConfig.sides
    : [];
  const isMultiSidedIdSelected = selectedIdTypeSides.length > 1;
  const currentIdUploadedDocs = selectedIdTypeSides.flatMap((side) => {
    const docsForSide = currentIdProofDocsBySide[side] || [];
    return docsForSide.map((doc) => ({
      ...doc,
      side: isMultiSidedIdSelected ? side : undefined,
    }));
  });
  const uploadedIdSides = selectedIdTypeSides.filter(
    (side) => (currentIdProofDocsBySide[side] || []).length > 0
  );
  const pendingIdSides = selectedIdTypeSides.filter(
    (side) => (currentIdProofDocsBySide[side] || []).length === 0
  );
  const shouldShowStackedFirstUploadLayout = isMultiSidedIdSelected
    && uploadedIdSides.length === 1
    && pendingIdSides.length === 1;
  const hasCurrentIdProofUploads = currentIdUploadedDocs.length > 0;
  const shouldShowInitialMultiSidedUploadLayout = isMultiSidedIdSelected
    && !hasCurrentIdProofUploads;
  const shouldShowFullyUploadedMultiSidedLayout = isMultiSidedIdSelected
    && selectedIdTypeSides.length > 0
    && uploadedIdSides.length === selectedIdTypeSides.length;
  const canAddAnotherIdProof = Boolean(selectedIdTypeConfig) && hasCurrentIdProofUploads;
  const hasSavedIdProofs = savedIdProofs.length > 0;
  const usedSavedIdTypeIds = new Set(
    savedIdProofs
      .map((proof) => proof.idType)
      .filter(Boolean)
  );
  const availableIdTypeOptions = idTypeOptions.filter(
    (docType) => !usedSavedIdTypeIds.has(docType.id) || docType.id === selectedIdType
  );
  const shouldShowIdProofEntry = !hasSavedIdProofs
    || isAddingAnotherIdProof
    || Boolean(selectedIdTypeConfig)
    || hasCurrentIdProofUploads;
  const shouldShowAddAnotherIdProofHeader = isAddingAnotherIdProof
    && hasSavedIdProofs
    && !hasCurrentIdProofUploads;
  const canStartAnotherIdProof = hasSavedIdProofs && !shouldShowIdProofEntry;
  const idProofUploadedCount = savedIdProofs.reduce(
    (totalDocs, proof) => totalDocs + proof.docs.length,
    0
  )
  + currentIdUploadedDocs.length;

  // ID Proof CSS class names
  const idProofInnerRowClassName = 'duak-id-proof-inner-row';
  const idProofActionContainerClassName = 'duak-id-proof-action-container';

  // ID Proof delete handlers
  const handleCurrentIdDelete = (fileId) => {
    onResetMessages();

    const target = currentIdUploadedDocs.find((uploadedDoc) => uploadedDoc.fileId === fileId);
    if (!target) {
      return Promise.resolve(null);
    }

    setIsDeleting(true);

    return deleteDocumentApi({
      dispatch,
      documentId,
      fileId: target.fileId,
      formDataProps,
    }).then(({ response: res, outcome: deleteOutcome }) => {
      setIsDeleting(false);
      if (deleteOutcome !== 'success') {
        setStatusMessage({ message: deleteOutcome, type: 'error' });
        return null;
      }

      if (onDeleteCall) {
        onDeleteCall(res);
      }

      const targetSide = target.uploadSide || target.side || selectedIdTypeSides[0];
      setCurrentIdProofDocsBySide((previousDocsBySide) => ({
        ...previousDocsBySide,
        [targetSide]: removeDocByFileId(previousDocsBySide[targetSide] || [], target.fileId),
      }));

      setShowPopup({
        idx: null,
        fileId: null,
        fileName: null,
        popupType: '',
      });

      return res;
    }).catch(() => {
      setIsDeleting(false);
      setStatusMessage({
        message: 'deleteApiError',
        type: 'error',
      });
      return null;
    });
  };

  // Wrapper for ID proof popup flow - phase 1: show popup
  const handleCurrentIdPopupShow = (...popupArgs) => {
    const [idx, fileId, fileName, show, popupType, confirm] = popupArgs;
    if (confirm) {
      const fallbackFileId = currentIdUploadedDocs[idx]?.fileId;
      return handleCurrentIdDelete(fileId || fallbackFileId);
    }
    setShowPopup({
      show,
      idx,
      fileId,
      fileName,
      popupType,
      action: 'currentId',
    });
    return null;
  };

  const handleCurrentIdSidePopupShow = (side) => (...popupArgs) => {
    const [idx, fileId, fileName, show, popupType, confirm] = popupArgs;
    if (confirm) {
      const docsForSide = currentIdProofDocsBySide[side] || [];
      const fallbackFileId = docsForSide[idx]?.fileId;
      return handleCurrentIdDelete(fileId || fallbackFileId);
    }
    setShowPopup({
      show,
      idx,
      fileId,
      fileName,
      popupType,
      action: 'currentId',
    });
    return null;
  };

  const handleSavedIdProofDelete = (proofIndex, idx) => {
    onResetMessages();

    const targetDoc = savedIdProofs[proofIndex]?.docs?.[idx];
    if (!targetDoc) {
      return Promise.resolve(null);
    }

    setIsDeleting(true);

    return deleteDocumentApi({
      dispatch,
      documentId,
      fileId: targetDoc.fileId,
      formDataProps,
    }).then(({ response: res, outcome: deleteOutcome }) => {
      setIsDeleting(false);
      if (deleteOutcome !== 'success') {
        setStatusMessage({ message: deleteOutcome, type: 'error' });
        return null;
      }

      if (onDeleteCall) {
        onDeleteCall(res);
      }

      setSavedIdProofs((previousProofs) => previousProofs
        .map((proof, currentProofIndex) => {
          if (currentProofIndex !== proofIndex) {
            return proof;
          }
          return {
            ...proof,
            docs: removeDocByIndex(proof.docs, idx),
          };
        })
        .filter((proof) => proof.docs.length > 0)
      );

      setShowPopup({
        idx: null,
        fileId: null,
        fileName: null,
        popupType: '',
      });

      return res;
    }).catch(() => {
      setIsDeleting(false);
      setStatusMessage({
        message: 'deleteApiError',
        type: 'error',
      });
      return null;
    });
  };

  // Wrapper for saved ID proof popup flow - stores proof index and uses unified handler
  const handleSavedIdProofPopupShow = (proofIndex) => (...popupArgs) => {
    const [idx, fileId, fileName, show, popupType, confirm] = popupArgs;
    if (confirm) {
      return handleSavedIdProofDelete(proofIndex, idx);
    }
    setShowPopup({
      show,
      idx,
      fileId,
      fileName,
      popupType,
      action: 'savedIdProof',
      proofIndex,
    });
    return null;
  };

  const handleAddAnotherIdProof = () => {
    if (!selectedIdTypeConfig || !hasCurrentIdProofUploads) {
      return;
    }

    const docsForCurrentProof = currentIdUploadedDocs;

    if (docsForCurrentProof.length === 0) {
      return;
    }

    const proofSnapshot = {
      key: `${selectedIdType}-${docsForCurrentProof.map((doc) => doc.fileId).join('-')}`,
      idType: selectedIdType,
      label: selectedIdTypeLabel,
      docs: docsForCurrentProof,
    };

    setSavedIdProofs((previousProofs) => [...previousProofs, proofSnapshot]);
    setCurrentIdProofDocsBySide({});
    setSelectedIdType('');
    setIsAddingAnotherIdProof(true);
  };

  const handleCancelAnotherIdProof = () => {
    setSelectedIdType('');
    setCurrentIdProofDocsBySide({});
    setIsAddingAnotherIdProof(false);
  };

  const handleStartAnotherIdProof = () => {
    setSelectedIdType('');
    setCurrentIdProofDocsBySide({});
    setIsAddingAnotherIdProof(true);
  };

  const renderSelectedIdSideLabel = (side) => {
    if (side === 'front') {
      return <FormattedMessage id="kycFront" />;
    }
    if (side === 'back') {
      return <FormattedMessage id="kycBackOptional" />;
    }
    return formatSideFallbackLabel(side);
  };

  const createSelectedIdSideOnDrop = (side) => (acceptedFiles, rejectedFiles = []) => {
    onResetMessages();

    if (rejectedFiles.length > 0) {
      setScopedStatusMessage(
        getRejectedFileStatus(rejectedFiles[0], maxFileSize, maxSizeLabel),
        'id-proof'
      );
    }

    if (!Array.isArray(acceptedFiles) || acceptedFiles.length === 0) {
      return Promise.resolve([]);
    }

    if (acceptedFiles.length > 1) {
      setScopedStatusMessage({
        message: 'multiFileError',
        type: 'error',
      }, 'id-proof');
      return Promise.resolve([]);
    }

    // Side-scoped pending doc tracks which side (front/back/etc.) the upload belongs to.
    const pendingDocs = acceptedFiles.map((file) => ({
      name: file.name,
      size: file.size,
      type: file.type,
      fileId: [
        'pending',
        file.name,
        file.size,
        file.lastModified,
        file.type,
        side,
      ].join('-'),
      progress: 0,
      uploadSide: side,
    }));

    setCurrentIdProofDocsBySide((previousDocsBySide) => {
      const existingDocs = previousDocsBySide[side] || [];
      return {
        ...previousDocsBySide,
        [side]: [...existingDocs, ...pendingDocs],
      };
    });

    const uploads = pendingDocs.map((pendingDoc, index) => uploadDocumentApi({
      docUploadUrl,
      formDataProps,
      file: acceptedFiles[index],
      documentId: documentId || 'INITIALDOC',
      onStartUploadCall,
      onProgress: (event) => {
        setCurrentIdProofDocsBySide((previousDocsBySide) => ({
          ...previousDocsBySide,
          [side]: (previousDocsBySide[side] || []).map((doc) => {
            if (doc.fileId === pendingDoc.fileId) {
              return {
                ...doc,
                progress: Number((event.loaded / event.total) * 100).toFixed(0),
              };
            }
            return doc;
          }),
        }));
      },
    }).then(({ response: res, outcome: uploadOutcome }) => {
      if (uploadOutcome !== 'success') {
        return null;
      }

      // Persist shared document id once the first side upload succeeds.
      setDocumentId((currentDocumentId) => currentDocumentId || res.documentId);

      if (onUploadCall) {
        onUploadCall(res);
      }

      setCurrentIdProofDocsBySide((previousDocsBySide) => ({
        ...previousDocsBySide,
        [side]: (previousDocsBySide[side] || []).map((doc) => {
          if (doc.fileId === pendingDoc.fileId) {
            return {
              ...doc,
              fileId: res.fileList[res.fileList.length - 1].fileId,
              progress: null,
            };
          }
          return doc;
        }),
      }));

      return res;
    }).catch(() => {
      // Remove failed side upload placeholder while preserving other sides/docs.
      setCurrentIdProofDocsBySide((previousDocsBySide) => ({
        ...previousDocsBySide,
        [side]: (previousDocsBySide[side] || []).filter(
          (doc) => doc.fileId !== pendingDoc.fileId
        ),
      }));
    }));

    return Promise.all(uploads);
  };

  const renderAccordionSection = ({
    sectionId,
    heading,
    uploadedCount,
    isExpanded,
    onToggle,
    children,
  }) => (
    <Accordion
      key={sectionId}
      data-testid={`doc-upload-kyc-${sectionId}-accordion`}
      className="width-full"
      isBordered={true}
      shouldAllowMultipleExpanded={false}
      openPanelIds={isExpanded ? [sectionId] : []}
      onAccordionToggle={onToggle}
    >
      <AccordionPanel
        id={sectionId}
        data-testid={`doc-upload-kyc-${sectionId}-header-container`}
        className="dls-white-bg"
        heading={(
          <div
            data-testid={`doc-upload-kyc-${sectionId}-label`}
            className="duak-accordion-heading-row flex flex-row flex-justify-between flex-align-center width-full"
          >
            <span className="duak-accordion-heading heading-3">{heading}</span>
            {uploadedCount > 0 ? (
              <span className="duak-accordion-uploaded-count flex flex-row flex-align-center margin-1-r">
                <span className="duak-accordion-uploaded-icon-container flex flex-justify-center flex-align-center">
                  <IconSuccess
                    size="md"
                    isFilled={true}
                    color="success"
                    title="uploaded files"
                  />
                </span>
                <span className="body font-weight-normal dls-positive-green duak-accordion-uploaded-text-color text-nowrap">
                  {`${uploadedCount} file${
                    uploadedCount === 1 ? '' : 's'
                  } uploaded`}
                </span>
              </span>
            ) : null}
          </div>
        )}
        chevronPosition="end"
      />
      <AccordionContent
        aria-labelledby={sectionId}
        data-testid={`doc-upload-kyc-${sectionId}-content-region`}
        className="dls-grey-01-bg duak-accordion-content-bg"
      >
        {children}
      </AccordionContent>
    </Accordion>
  );

  const fullWidthStyle = {
    width: '100%',
  };

  const renderStandardProofSection = (section) => {
    const sectionId = section.id;
    const acceptedDocs = getSectionAcceptedDocs(sectionId);
    const hasAcceptedDocs = acceptedDocs.length > 0;
    const isSingleUploadSection = isSectionSingleUpload(sectionId);
    const isMultipleAllowedForSection = !isSingleUploadSection;
    const isAddingAnotherForSection = Boolean(isAddingAnotherBySection[sectionId]);
    const fallbackLabel = formatSectionFallbackLabel(sectionId);

    return renderAccordionSection({
      sectionId,
      heading: renderSectionLabel(section, null, fallbackLabel),
      uploadedCount: acceptedDocs.length,
      isExpanded: Boolean(expandedSectionIds[sectionId]),
      onToggle: handleSectionAccordionToggle(sectionId),
      children: (
        <div
          data-testid={`doc-upload-kyc-${sectionId}-content-container`}
          className={`flex flex-column flex-justify-center flex-align-end flex-align-self-stretch width-full pad-3 radius-none ${
            hasAcceptedDocs
              ? 'duak-proof-panel-content'
              : 'duak-single-upload-section-content'
          }`}
        >
          {hasAcceptedDocs ? (
            <>
              <div
                data-testid={`doc-upload-kyc-${sectionId}-inner-row`}
                className={`duak-proof-list-row flex flex-row flex-align-start width-full ${isSingleUploadSection && isAddingAnotherForSection ? 'duak-single-upload-list-row-expanded' : ''}`}
              >
                <div
                  data-testid={`doc-upload-kyc-${sectionId}-file-list-column`}
                  className={`${isSingleUploadSection ? 'width-full' : 'duak-proof-list-left-column'} flex flex-column flex-justify-center flex-align-end`}
                >
                  <DocList
                    acceptedDocs={acceptedDocs}
                    handlePopupShow={handlePopupShow(sectionId)}
                    widthStyle={fullWidthStyle}
                    docListView={docListView}
                    filePreview={filePreview}
                    documentId={documentId}
                    downloadProgress={downloadProgress}
                  />
                </div>
                {!isSingleUploadSection ? (
                  <div
                    data-testid={`doc-upload-kyc-${sectionId}-upload-zone`}
                    className="duak-proof-list-upload-zone-container flex flex-column flex-justify-center flex-align-center radius-container border-dark-dashed dls-white-bg"
                  >
                    <UploadZone
                      onDrop={handleSectionOnDrop(sectionId)}
                      widthStyle={fullWidthStyle}
                      sumOfDocs={0}
                      isDropZoneDisabled={isSingleUploadSection ? true : isUploadDisabled}
                      maxFileSize={maxFileSize}
                      maxBatchSize={maxBatchSize}
                      fileTypes={fileTypes}
                      uploadZoneView={uploadZoneView}
                      isKycView={true}
                      isMultipleAllowed={isMultipleAllowedForSection}
                    />
                  </div>
                ) : null}
              </div>

              {isSingleUploadSection && isAddingAnotherForSection ? (
                <div
                  data-testid={`doc-upload-kyc-${sectionId}-add-another-upload-zone-row`}
                  className="duak-single-upload-section-inner flex flex-row flex-align-start width-full"
                >
                  <div
                    data-testid={`doc-upload-kyc-${sectionId}-upload-zone`}
                    className="duak-single-upload-zone-wrapper flex flex-column flex-justify-center flex-align-center radius-container border-dark-dashed dls-white-bg"
                  >
                    <UploadZone
                      onDrop={handleSectionOnDrop(sectionId)}
                      widthStyle={fullWidthStyle}
                      sumOfDocs={0}
                      isDropZoneDisabled={isUploadDisabled}
                      maxFileSize={maxFileSize}
                      maxBatchSize={maxBatchSize}
                      fileTypes={fileTypes}
                      uploadZoneView={uploadZoneView}
                      isKycView={true}
                      isMultipleAllowed={isMultipleAllowedForSection}
                    />
                  </div>
                </div>
              ) : null}
            </>
          ) : (
            <div
              data-testid={`doc-upload-kyc-${sectionId}-inner-row`}
              className="duak-single-upload-section-inner flex flex-row flex-align-start width-full"
            >
              <div
                data-testid={`doc-upload-kyc-${sectionId}-upload-zone`}
                className="duak-single-upload-zone-wrapper flex flex-column flex-justify-center flex-align-center radius-container border-dark-dashed dls-white-bg"
              >
                <UploadZone
                  onDrop={handleSectionOnDrop(sectionId)}
                  widthStyle={fullWidthStyle}
                  sumOfDocs={0}
                  isDropZoneDisabled={isUploadDisabled}
                  maxFileSize={maxFileSize}
                  maxBatchSize={maxBatchSize}
                  fileTypes={fileTypes}
                  uploadZoneView={uploadZoneView}
                  isKycView={true}
                  isMultipleAllowed={isMultipleAllowedForSection}
                />
              </div>
            </div>
          )}

          {isSingleUploadSection && hasAcceptedDocs && !isAddingAnotherForSection ? (
            <div
              data-testid={`doc-upload-kyc-${sectionId}-add-another-container`}
              className="duak-add-another-id-proof-container flex flex-justify-end flex-align-center"
            >
              <Button
                data-testid={`doc-upload-kyc-${sectionId}-add-another-button`}
                variant="secondary"
                className="duak-add-another-id-proof-button"
                onClick={() => {
                  setIsAddingAnotherBySection((previousState) => ({
                    ...previousState,
                    [sectionId]: true,
                  }));
                }}
              >
                <FormattedMessage id="kycAddAnotherDocument" defaultMessage="Add Another Document" />
              </Button>
            </div>
          ) : null}

          {statusSectionId === sectionId && statusMessage.message ? (
            <ResponseMessages
              statusMessage={statusMessage}
              errorLabel=""
            />
          ) : null}
        </div>
      ),
    });
  };

  const allUploadedDocs = [
    ...savedIdProofs.flatMap((proof) => proof.docs),
    ...Object.values(currentIdProofDocsBySide).flat(),
    ...Object.values(sectionAcceptedDocs).flat(),
  ];
  const totalUploadedSize = calculateDocsTotalSize(allUploadedDocs);
  const hasUploadedDocs = totalUploadedSize > 0;

  return (
    <div
      data-testid="doc-upload-kyc-outer-container"
      className="doc-upload-width-shell duak-outer-container duak-flow-kyc flex flex-column flex-align-start flex-align-self-stretch"
    >
      <div
        data-testid="doc-upload-kyc-header-block"
        className="duak-header-block flex flex-column flex-align-start width-full"
      >
        <div
          data-testid="doc-upload-kyc-title-row"
          className="duak-title-row flex flex-align-start flex-item-grow width-full"
        >
          <span className="duak-title-label label font-weight-medium">
            <FormattedMessage id="kycUploadDocumentTitle" />
          </span>
        </div>
        <div
          data-testid="doc-upload-kyc-description-block"
          className="duak-header-block flex flex-column flex-align-start width-full"
        >
          <p className="duak-description-text dls-grey-06 font-weight-normal flex-align-self-stretch width-full">
            <FormattedMessage id="selectFileHelperText" />
          </p>
          <FileRequirements
            id="docUploadKycFileRequirements"
            widthStyle={fullWidthStyle}
            maxSizeLabel={maxSizeLabel}
            maxBatchSizeLabel={maxBatchSizeLabel}
            fileTypesLabel={fileTypesLabel}
            fileReqView={fileReqView}
          />
        </div>
      </div>
      <div
        data-testid="doc-upload-kyc-upload-zone-container"
        className="duak-upload-zone-container flex flex-column flex-align-start width-full radius-container border-dark dls-white-bg"
      >
        {/* ID Proof Accordion Section */}
        {/* eslint-disable-next-line complexity -- preserve current UI rendering behavior */}
        {(() => {
          const idProofSection = kycDocumentSections.find(
            (section) => section.id === 'id-proof'
          );

          return idProofSection && idTypeOptions.length > 0
            ? renderAccordionSection({
              sectionId: 'id-proof',
              heading: renderSectionLabel(
                idProofSection,
                'kycIdProof',
                'ID Proof'
              ),
              uploadedCount: idProofUploadedCount,
              isExpanded: isIdProofExpanded,
              onToggle: () => {
                setIsIdProofExpanded((prev) => !prev);
              },
              children: (
                <div
                  data-testid="doc-upload-kyc-id-proof-content-container"
                  className={`duak-id-proof-content-container ${
                    isMultiSidedIdSelected
                      ? 'duak-id-proof-content-container-dual'
                      : ''
                  } flex flex-column flex-align-start flex-align-self-stretch width-full pad-3 radius-none`}
                >
                  {/* Saved ID Proofs */}
                  {savedIdProofs.map((savedProof, proofIndex) => (
                    <div
                      key={savedProof.key}
                      data-testid="doc-upload-kyc-id-proof-saved-proof-container"
                      className={`duak-completed-state-id-action-container ${proofIndex > 0 ? 'duak-completed-state-id-action-container-with-divider' : ''} flex flex-column flex-align-start width-full`}
                    >
                      <div
                        data-testid="doc-upload-kyc-id-proof-saved-proof-inner-row"
                        className="duak-id-proof-inner-row flex flex-row flex-align-start flex-align-self-stretch"
                      >
                        <div
                          data-testid="doc-upload-kyc-id-proof-saved-proof-action-container"
                          className="duak-id-proof-action-container flex flex-column flex-align-start"
                        >
                          <div
                            data-testid="doc-upload-kyc-id-proof-saved-id-type-wrap"
                            className="duak-id-type-wrap-container flex flex-align-start flex-align-content-start flex-align-self-stretch flex-wrap width-full"
                          >
                            <div
                              data-testid="doc-upload-kyc-id-proof-saved-id-type-label"
                              className="duak-id-type-label"
                            >
                              <FormattedMessage id="kycIdType" />
                            </div>
                          </div>
                          <SelectNative
                            id={`doc-upload-kyc-saved-id-type-dropdown-${proofIndex}`}
                            data-testid="doc-upload-kyc-saved-id-type-dropdown"
                            aria-label="Saved ID Type"
                            className="duak-id-type-select width-full"
                            value={savedProof.idType || ''}
                            disabled={true}
                          >
                            <SelectNativeOption value="" disabled={true}>
                              {intl.formatMessage({ id: 'kycSelect' })}
                            </SelectNativeOption>
                            {idTypeOptions.map((docType) => (
                              <SelectNativeOption
                                key={docType.id}
                                value={docType.id}
                              >
                                {docType.label}
                              </SelectNativeOption>
                            ))}
                            {!idTypeOptions.some((docType) => docType.id === savedProof.idType) ? (
                              <SelectNativeOption value={savedProof.idType || ''}>
                                {savedProof.label}
                              </SelectNativeOption>
                            ) : null}
                          </SelectNative>
                        </div>
                        <div
                          data-testid="doc-upload-kyc-id-proof-saved-proof-file-list-column"
                          className="duak-passport-uploaded-list-container flex flex-column flex-justify-center flex-align-end"
                        >
                          <div className="duak-proof-heading">
                            <FormattedMessage id="kycUploaded" defaultMessage="Uploaded" />
                          </div>
                          <DocList
                            acceptedDocs={savedProof.docs}
                            handlePopupShow={handleSavedIdProofPopupShow(
                              proofIndex
                            )}
                            widthStyle={fullWidthStyle}
                            docListView={docListView}
                            suppressProofHeading={true}
                            filePreview={filePreview}
                            documentId={documentId}
                            downloadProgress={downloadProgress}
                          />
                        </div>
                      </div>
                    </div>
                  ))}

                  {/* Current ID Proof Entry */}
                  {shouldShowIdProofEntry ? (
                    <div
                      data-testid="doc-upload-kyc-id-proof-entry-container"
                      className={`duak-id-proof-entry-container ${
                        hasSavedIdProofs ? 'duak-id-proof-entry-container-with-divider' : ''
                      } flex flex-column width-full`}
                    >
                      {shouldShowAddAnotherIdProofHeader ? (
                        <div
                          data-testid="doc-upload-kyc-id-type-close-container"
                          className="duak-id-type-close-container flex flex-row flex-justify-end width-full"
                        >
                          <IconButton
                            data-testid="doc-upload-kyc-id-type-close-button"
                            className="duak-id-type-close-button"
                            variant="tertiary"
                            screenReaderLabel="Close additional ID proof"
                            aria-label="Close additional ID proof"
                            onClick={handleCancelAnotherIdProof}
                          >
                            <IconClose
                              title="Close additional ID proof"
                            />
                          </IconButton>
                        </div>
                      ) : null}

                      <div
                        data-testid="doc-upload-kyc-id-proof-inner-row"
                        className={`${idProofInnerRowClassName} flex flex-row flex-align-start flex-align-self-stretch`}
                      >
                        <div
                          data-testid="doc-upload-kyc-id-proof-action-container"
                          className={`${idProofActionContainerClassName} flex flex-column flex-align-start`}
                        >
                          <div
                            data-testid="doc-upload-kyc-id-type-wrap-container"
                            className="duak-id-type-wrap-container flex flex-align-start flex-align-content-start flex-align-self-stretch flex-wrap width-full"
                          >
                            <div
                              data-testid="doc-upload-kyc-id-type-label"
                              className="duak-id-type-label"
                            >
                              <FormattedMessage id="kycIdType" />
                            </div>
                          </div>
                          <SelectNative
                            id="doc-upload-kyc-id-type-dropdown"
                            data-testid="doc-upload-kyc-id-type-dropdown"
                            aria-label="ID Type"
                            className="duak-id-type-select width-full"
                            value={selectedIdType}
                            disabled={isUploadDisabled || hasCurrentIdProofUploads}
                            onChange={(event) => setSelectedIdType(event.target.value)}
                          >
                            <SelectNativeOption value="" disabled={true}>
                              {intl.formatMessage({ id: 'kycSelect' })}
                            </SelectNativeOption>
                            {availableIdTypeOptions.map((docType) => (
                              <SelectNativeOption
                                key={docType.id}
                                value={docType.id}
                              >
                                {docType.label}
                              </SelectNativeOption>
                            ))}
                          </SelectNative>

                        </div>

                        {/* Multi-Sided Initial Upload Slots */}
                        {shouldShowInitialMultiSidedUploadLayout ? (
                          <div
                            data-testid="doc-upload-kyc-id-proof-state-uploads-row"
                            className="duak-id-proof-state-uploads-row flex flex-row flex-align-start"
                          >
                            {selectedIdTypeSides.map((side) => {
                              const docsForSide = currentIdProofDocsBySide[side] || [];
                              const docsForSideWithLabel = docsForSide.map((doc) => ({
                                ...doc,
                                side,
                              }));
                              return (
                                <div
                                  key={side}
                                  data-testid={`doc-upload-kyc-id-proof-${side}-container`}
                                  className="duak-id-proof-info-container flex flex-column flex-align-start dls-grey-06"
                                >
                                  <div
                                    data-testid={`doc-upload-kyc-id-proof-${side}-label`}
                                    className="duak-id-proof-upload-label dls-grey-05 font-weight-bold text-align-center"
                                  >
                                    {renderSelectedIdSideLabel(side)}
                                  </div>
                                  {docsForSide.length > 0 ? (
                                    <div
                                      data-testid={`doc-upload-kyc-id-proof-${side}-file-list`}
                                      className="duak-id-proof-side-file-list-container flex flex-column flex-justify-center flex-align-end"
                                    >
                                      <DocList
                                        acceptedDocs={docsForSideWithLabel}
                                        handlePopupShow={handleCurrentIdSidePopupShow(side)}
                                        widthStyle={fullWidthStyle}
                                        docListView={docListView}
                                        suppressProofHeading={true}
                                        filePreview={filePreview}
                                        documentId={documentId}
                                        downloadProgress={downloadProgress}
                                      />
                                    </div>
                                  ) : (
                                    <div
                                      data-testid={`doc-upload-kyc-id-proof-${side}-upload-zone`}
                                      className="duak-id-proof-upload-zone-wrapper flex flex-column flex-justify-center flex-align-center radius-container border-dark-dashed dls-white-bg"
                                    >
                                      <UploadZone
                                        onDrop={createSelectedIdSideOnDrop(side)}
                                        widthStyle={fullWidthStyle}
                                        sumOfDocs={0}
                                        isDropZoneDisabled={isUploadDisabled}
                                        maxFileSize={maxFileSize}
                                        maxBatchSize={maxBatchSize}
                                        fileTypes={fileTypes}
                                        uploadZoneView={uploadZoneView}
                                        isKycView={true}
                                        isMultipleAllowed={false}
                                      />
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        ) : null}

                        {/* Multi-Sided First Upload Layout */}
                        {shouldShowStackedFirstUploadLayout ? (
                          <div
                            data-testid="doc-upload-kyc-id-proof-state-stacked-layout"
                            className="duak-id-proof-state-stacked-layout flex flex-column flex-align-start"
                          >
                            <div
                              data-testid={`doc-upload-kyc-id-proof-${pendingIdSides[0]}-container`}
                              className="duak-id-proof-info-container duak-id-proof-stacked-slot flex flex-column flex-align-start dls-grey-06"
                            >
                              <div
                                data-testid={`doc-upload-kyc-id-proof-${pendingIdSides[0]}-label`}
                                className="duak-id-proof-upload-label dls-grey-05 font-weight-bold text-align-center"
                              >
                                {renderSelectedIdSideLabel(pendingIdSides[0])}
                              </div>
                              <div
                                data-testid={`doc-upload-kyc-id-proof-${pendingIdSides[0]}-upload-zone`}
                                className="duak-id-proof-upload-zone-wrapper duak-id-proof-upload-zone-stacked flex flex-column flex-justify-center flex-align-center radius-container border-dark-dashed dls-white-bg"
                              >
                                <UploadZone
                                  onDrop={createSelectedIdSideOnDrop(pendingIdSides[0])}
                                  widthStyle={fullWidthStyle}
                                  sumOfDocs={0}
                                  isDropZoneDisabled={isUploadDisabled}
                                  maxFileSize={maxFileSize}
                                  maxBatchSize={maxBatchSize}
                                  fileTypes={fileTypes}
                                  uploadZoneView={uploadZoneView}
                                  isKycView={true}
                                  isMultipleAllowed={false}
                                />
                              </div>
                            </div>

                            <div
                              data-testid={`doc-upload-kyc-id-proof-${uploadedIdSides[0]}-file-list`}
                              className="duak-id-proof-stacked-file-list-container flex flex-column flex-align-start"
                            >
                              <div className="duak-id-proof-side-file-list-container duak-id-proof-side-file-list-stacked flex flex-column flex-justify-center flex-align-end">
                                <DocList
                                  acceptedDocs={(
                                    currentIdProofDocsBySide[uploadedIdSides[0]] || []
                                  ).map((doc) => ({
                                    ...doc,
                                    side: uploadedIdSides[0],
                                  }))}
                                  handlePopupShow={handleCurrentIdSidePopupShow(uploadedIdSides[0])}
                                  widthStyle={fullWidthStyle}
                                  docListView={docListView}
                                  suppressProofHeading={true}
                                  filePreview={filePreview}
                                  documentId={documentId}
                                  downloadProgress={downloadProgress}
                                />
                              </div>
                            </div>
                          </div>
                        ) : null}

                        {/* Multi-Sided Fully Uploaded Layout */}
                        {shouldShowFullyUploadedMultiSidedLayout ? (
                          <div
                            data-testid="doc-upload-kyc-id-proof-uploaded-column"
                            className="duak-id-proof-uploaded-column flex flex-column flex-align-start"
                          >
                            <div className="duak-proof-heading">
                              <FormattedMessage id="kycUploaded" defaultMessage="Uploaded" />
                            </div>
                            <div
                              data-testid="doc-upload-kyc-id-proof-passport-file-list-column"
                              className="duak-passport-uploaded-list-container flex flex-column flex-justify-center flex-align-end"
                            >
                              <DocList
                                acceptedDocs={currentIdUploadedDocs}
                                handlePopupShow={handleCurrentIdPopupShow}
                                widthStyle={fullWidthStyle}
                                docListView={docListView}
                                suppressProofHeading={true}
                                filePreview={filePreview}
                                documentId={documentId}
                                downloadProgress={downloadProgress}
                              />
                            </div>
                          </div>
                        ) : null}

                        {/* Current ID Uploaded Docs - Single Sided */}
                        {!isMultiSidedIdSelected && hasCurrentIdProofUploads ? (
                          <div
                            data-testid="doc-upload-kyc-id-proof-uploaded-column"
                            className="duak-id-proof-uploaded-column flex flex-column flex-align-start"
                          >
                            <div
                              data-testid="doc-upload-kyc-id-proof-passport-file-list-column"
                              className="duak-passport-uploaded-list-container flex flex-column flex-justify-center flex-align-end"
                            >
                              <DocList
                                acceptedDocs={currentIdUploadedDocs}
                                handlePopupShow={handleCurrentIdPopupShow}
                                widthStyle={fullWidthStyle}
                                docListView={docListView}
                                filePreview={filePreview}
                                documentId={documentId}
                                downloadProgress={downloadProgress}
                              />
                            </div>
                          </div>
                        ) : null}

                        {/* Single-Sided Upload Zone */}
                        {!isMultiSidedIdSelected
                          && selectedIdTypeSides.length > 0
                          && !hasCurrentIdProofUploads ? (
                            <div
                              data-testid="doc-upload-kyc-id-proof-passport-container"
                              className="duak-passport-upload-container flex flex-column flex-align-start dls-grey-06"
                            >
                              <div
                                data-testid="doc-upload-kyc-passport-upload-zone"
                                className="duak-passport-upload-zone-wrapper flex flex-column flex-justify-center flex-align-center radius-container border-dark-dashed dls-white-bg"
                              >
                                <UploadZone
                                  onDrop={createSelectedIdSideOnDrop(selectedIdTypeSides[0])}
                                  widthStyle={fullWidthStyle}
                                  sumOfDocs={0}
                                  isDropZoneDisabled={isUploadDisabled}
                                  maxFileSize={maxFileSize}
                                  maxBatchSize={maxBatchSize}
                                  fileTypes={fileTypes}
                                  uploadZoneView={uploadZoneView}
                                  isKycView={true}
                                  isMultipleAllowed={false}
                                />
                              </div>
                            </div>
                          ) : null}
                      </div>
                    </div>
                  ) : null}

                  {/* Add Another ID Proof Button */}
                  {canAddAnotherIdProof || canStartAnotherIdProof ? (
                    <div
                      data-testid="doc-upload-kyc-id-proof-add-another-container"
                      className="duak-add-another-id-proof-container flex flex-justify-end flex-align-center"
                    >
                      <Button
                        data-testid="doc-upload-kyc-id-proof-add-another-button"
                        variant="secondary"
                        className="duak-add-another-id-proof-button"
                        onClick={canAddAnotherIdProof
                          ? handleAddAnotherIdProof
                          : handleStartAnotherIdProof}
                      >
                        <FormattedMessage id="kycAddAnotherIdProof" />
                      </Button>
                    </div>
                  ) : null}

                  {statusSectionId === 'id-proof' && statusMessage.message ? (
                    <ResponseMessages
                      statusMessage={statusMessage}
                      errorLabel=""
                    />
                  ) : null}
                </div>
              ),
            })
            : null;
        })()}

        {/* Standard Sections */}
        {(() => {
          const dynamicNonIdSections = kycDocumentSections.filter(
            (section) => section.id !== 'id-proof'
          );
          const hasSupportingDocumentSection = dynamicNonIdSections.some(
            (section) => section.id === 'supporting-document'
          );
          const supportingDocumentSection = {
            id: 'supporting-document',
            label: null,
            labelMessageId: 'kycSupportingDocument',
          };
          return (
            <>
              {dynamicNonIdSections.map(renderStandardProofSection)}
              {!hasSupportingDocumentSection
                ? renderStandardProofSection(supportingDocumentSection)
                : null}
            </>
          );
        })()}
      </div>
      {statusSectionId === '' && statusMessage.message ? (
        <ResponseMessages
          statusMessage={statusMessage}
          errorLabel=""
        />
      ) : null}
      <Submit
        sumOfDocs={totalUploadedSize}
        submitDoc={submitDoc}
        handlePopupShow={(...popupArgs) => handlePopupShow('submit')(...popupArgs)}
        widthStyle={fullWidthStyle}
        isSubmitting={isSubmitting}
        isSubmitDisabled={isSubmitDisabled || !hasUploadedDocs}
        submitView={submitView}
        maxBatchSizeLabel={maxBatchSizeLabel}
        submitPopLabel={submitPopLabel}
      />
      <DeleteConfirmation
        showPopup={showPopup}
        handlePopupShow={(...popupArgs) => {
          if (showPopup.action === 'currentId') {
            return handleCurrentIdPopupShow(...popupArgs);
          }
          if (showPopup.action === 'savedIdProof') {
            return handleSavedIdProofPopupShow(showPopup.proofIndex)(...popupArgs);
          }
          // Default to standard section handler (showPopup.sectionId or fallback for submit)
          const sectionId = showPopup.sectionId || 'standard';
          return handlePopupShow(sectionId)(...popupArgs);
        }}
        submitPopLabel={submitPopLabel || ''}
      />
    </div>
  );
}

DocUploadAlwaysKyc.defaultProps = {
  maxFileSize: DEFAULT_MAX_FILE_SIZE,
  maxBatchSize: DEFAULT_MAX_BATCH_SIZE,
  maxSizeLabel: DEFAULT_MAX_SIZE_LABEL,
  maxBatchSizeLabel: DEFAULT_MAX_BATCH_SIZE_LABEL,
  fileTypes: DEFAULT_ACCEPTED_FILE_TYPES,
  fileTypesLabel: DEFAULT_ACCEPTED_FILE_TYPES,
  isSubmitDisabled: false,
  isUploadDisabled: false,
  submitPopLabel: null,
  uploadZoneView: DEFAULT_UPLOAD_ZONE_VIEW,
  fileReqView: DEFAULT_FILE_REQ_VIEW,
  docListView: DEFAULT_DOC_LIST_VIEW,
  submitView: DEFAULT_SUBMIT_VIEW,
  idTypeMetadataApi: null,
  kycDocumentMetadataApi: null,
  formDataProps: null,
  docUploadUrl: null,
  onStartUploadCall: null,
  onUploadCall: null,
  onDeleteCall: null,
  filePreview: null,
  onSubmitCall: null,
  downloadProgress: null,
  sectionUploadModeById: null,
};

DocUploadAlwaysKyc.propTypes = {
  maxFileSize: PropTypes.number,
  maxBatchSize: PropTypes.number,
  maxSizeLabel: PropTypes.string,
  maxBatchSizeLabel: PropTypes.string,
  fileTypes: PropTypes.string,
  fileTypesLabel: PropTypes.string,
  isSubmitDisabled: PropTypes.bool,
  isUploadDisabled: PropTypes.bool,
  submitPopLabel: PropTypes.string,
  uploadZoneView: PropTypes.string,
  fileReqView: PropTypes.string,
  docListView: PropTypes.string,
  submitView: PropTypes.string,
  idTypeMetadataApi: PropTypes.func,
  kycDocumentMetadataApi: PropTypes.func,
  formDataProps: PropTypes.instanceOf(Object),
  docUploadUrl: PropTypes.string,
  onStartUploadCall: PropTypes.func,
  onUploadCall: PropTypes.func,
  onDeleteCall: PropTypes.func,
  filePreview: PropTypes.func,
  onSubmitCall: PropTypes.func,
  downloadProgress: PropTypes.instanceOf(Object),
  sectionUploadModeById: PropTypes.instanceOf(Object),
};

export default DocUploadAlwaysKyc;
